import { useInfiniteQuery } from '@tanstack/react-query'
import { type Document, type Filter, type Sort } from 'mongodb'
import { aDrawnFeature, aOrganization, aUser, useAtomValue } from '~/atoms'
import { toast } from '~/components/ui'
import { useEmbed } from '~/hooks'
import {
  getOrganizationSlugFilter,
  type Listing,
  type ModelFind,
} from '~/models'
import type { ListingTextIndex } from '~/mongo.server'
import type { ApiResponse } from './utils'

export async function aggregateListings(
  aggregate: Document[]
): Promise<ApiResponse & { results: any }> {
  const response = await fetch('/api/listings/aggregate', {
    body: JSON.stringify({ aggregate }),
    method: 'POST',
  })
  const result = await response.json()
  if (!result.success) {
    console.error('Error aggregating listings', result.error)
    throw new Error(`There was a problem aggregating results: ${result.error}`)
  }
  return result
}

type FetchListingsResponse = ApiResponse & {
  listings: Listing[]
  count: number
  nextOffset: number
}

export async function fetchListings({
  find,
  sort,
  project,
  limit,
  offset,
  signal,
}: {
  find: ModelFind
  sort?: Sort
  project?: Document
  limit?: number
  offset?: number
  signal?: AbortSignal
}): Promise<FetchListingsResponse> {
  const response = await fetch('/api/listings', {
    body: JSON.stringify({
      find,
      sort,
      project,
      limit,
      offset,
    }),
    method: 'POST',
    signal,
  })
  const result = await response.json()
  if (!result.success) {
    console.error('Error loading properties', result.error)
    throw new Error(`There was a problem fetching results: ${result.error}`)
  }
  return result
}

export async function fetchListingsWithSearch({
  searchText,
  indexName,
  find,
  sort,
  limit,
  offset,
  signal,
}: {
  searchText?: string
  indexName: ListingTextIndex
  find: ModelFind
  sort?: Sort
  limit?: number
  offset?: number
  signal?: AbortSignal
}): Promise<FetchListingsResponse> {
  const response = await fetch('/api/listings/search', {
    body: JSON.stringify({
      searchText,
      indexName,
      find,
      sort,
      limit,
      offset,
    }),
    method: 'POST',
    signal,
  })

  const result = await response.json()

  if (!result.success) {
    console.error('Error searching listings with Atlas', result.error)

    throw new Error(
      `There was a problem searching listings: ${result.error || 'Unknown error'}`
    )
  }

  return result
}

export function useListingsQuery({
  key,
  sort,
  find,
  limit,
  project,
  offset,
  enabled = true,
  refetchOnWindowFocus = false,
  refetchOnMount = false,
  refetchInterval,
}: {
  key: string[]
  find?: Filter<Listing>
  sort?: Sort
  limit?: number
  project?: Document
  offset?: number
  enabled?: boolean
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
  refetchInterval?: number
}) {
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const { isEmbed } = useEmbed()

  const listingsQuery = useInfiniteQuery({
    retry(failureCount) {
      if (failureCount === 0) {
        toast({
          variant: 'destructive',
          title: 'Error Fetching Listings',
          description: drawnFeature
            ? 'Please simplify your drawing.'
            : 'There was a problem fetching listings',
        })
        if (drawnFeature) {
          return false
        }
      }

      return true
    },
    queryKey: key,
    queryFn: ({ signal, pageParam = 0 }) => {
      const filterFind =
        find?.['organization.slug'] || find?.['organization._id']
          ? {
              isPublic: !user ? { $ne: false } : undefined,
              ...(find as Filter<Listing> &
                (
                  | { 'organization.slug': string }
                  | { 'organization._id': string }
                )),
            }
          : {
              $or: [
                {
                  'organization.slug':
                    isEmbed || !user
                      ? organization!.slug
                      : getOrganizationSlugFilter(organization!),
                },
                { externalMembershipSlugs: { $in: [organization!.slug] } },
              ],
              isPublic: !user ? { $ne: false } : undefined,
              ...(find as Record<string, any>),
            }
      return fetchListings({
        find: filterFind,
        sort,
        project,
        limit,
        offset: offset ?? pageParam,
        signal,
      })
    },
    enabled,
    refetchOnWindowFocus,
    refetchOnMount,
    // keepPreviousData: true,
    refetchInterval,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => lastPage.nextOffset,
    // onError: (error) => {
    //   console.error('Error loading global listings', error)
    // },
  })

  return {
    isLoading:
      listingsQuery.isLoading ||
      listingsQuery.isFetching ||
      listingsQuery.isRefetching ||
      (listingsQuery.isPending && enabled),
    // ||listingsQuery.isFetching,
    isFetching: listingsQuery.isFetching,
    // || listingsQuery.isRefetching,
    isRefetching: listingsQuery.isRefetching,
    isError: listingsQuery.isError,
    listings: listingsQuery.data?.pages?.map((p) => p.listings).flat() || [],
    pages: listingsQuery.data?.pages || [],
    count: listingsQuery.data?.pages?.[0].count ?? 0,
    refetch: listingsQuery.refetch,
    fetchNextPage: listingsQuery.fetchNextPage,
    hasNextPage: listingsQuery.hasNextPage,
    hasPreviousPage: listingsQuery.hasPreviousPage,
    isFetchingNextPage: listingsQuery.isFetchingNextPage,
  }
}
